import React, { useState, useCallback, useRef } from 'react';
import { Typography, Grid, Button, Container, Card, CardContent, Box, CircularProgress, Chip, LinearProgress } from '@material-ui/core';
import { ArrowBack, AttachFile, GetApp, CheckCircle, Error, Delete, Refresh } from '@material-ui/icons';
import { DataGridPro, GridColDef, GridCellParams } from '@mui/x-data-grid-pro';
import { componentsPropsValues, localizedTextsMap, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { addAlert } from 'store/modules/snackBar';
import { MdSpace } from 'm2m.components';
import { FileService, ParsedFileResult } from 'services/fileService';
import { AdicionarBloqueioEmLoteRequest, BloqueioTipo } from 'common/genericTypes';
import { CpfCnpjMask } from 'components/masks';

interface BloqueiosGarantiaEmLoteProps {
  onVoltar?: () => void;
}

interface BloqueioGarantiaData {
  codigo: string;
  custodianteGarantidor?: string;
  cpfCnpjInvestidorGarantidor?: string;
  contaDepositoGarantidor?: string;
  codigoAtivo?: string;
  status?: 'success' | 'error' | 'pending';
  mensagemErro?: string;
}

const BloqueiosGarantiaEmLote: React.FC<BloqueiosGarantiaEmLoteProps> = ({ onVoltar }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const investidorApi = useSelector((state: RootState) => state.investidorApi);

  const [file, setFile] = useState<File | undefined>();
  const [highlight, setHighlight] = useState<boolean>(false);
  const [dadosProcessados, setDadosProcessados] = useState<BloqueioGarantiaData[]>([]);
  const [processamentoCompleto, setProcessamentoCompleto] = useState<boolean>(false);
  const [errosValidacao, setErrosValidacao] = useState<string[]>([]);
  const [validandoArquivo, setValidandoArquivo] = useState<boolean>(false);
  const [enviandoParaBackend, setEnviandoParaBackend] = useState<boolean>(false);
  const [progressoEnvio, setProgressoEnvio] = useState<number>(0);

  const acceptedFileTypes = ['.xlsx', '.xls'];
  const templateHeaders = ['Código'];

  const handleVoltar = useCallback(() => {
    if (onVoltar) {
      onVoltar();
    } else {
      history.goBack();
    }
  }, [onVoltar, history]);

  const handleBaixarPlanilhaModelo = useCallback(() => {
    window.open("https://laqus-static-prod.s3.sa-east-1.amazonaws.com/solicitacoes/Modelo_Bloqueio_Em_Lote.xlsx", '_blank');
  }, []);

  const isValidFileType = (files: File[]): boolean => {
    return files.every(file => {
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      return acceptedFileTypes.includes(extension);
    });
  };

  const isValidFileSize = (file: File): boolean => {
    const maxSizeInMB = 10;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    return file.size <= maxSizeInBytes;
  };

  const handleFileTypeError = () => {
    dispatch(addAlert({
      severity: 'error',
      message: `Tipo de arquivo não suportado. Aceitos: ${acceptedFileTypes.join(', ')}`
    }));
  };

  const handleFileSizeError = () => {
    dispatch(addAlert({
      severity: 'error',
      message: 'Arquivo muito grande. Tamanho máximo permitido: 10MB'
    }));
  };

  const openFileDialog = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const processarArquivo = useCallback(async (arquivo: File) => {
    try {
      setValidandoArquivo(true);
      setErrosValidacao([]);

      const dadosParsed = await FileService.parseFile(arquivo, templateHeaders);
      const dadosValidados = validarDadosArquivo(dadosParsed);
      setDadosProcessados(dadosValidados.dados);
      
      if (dadosValidados.erros.length > 0) {
        setErrosValidacao(dadosValidados.erros);
        dispatch(addAlert({
          severity: 'warning',
          message: `Arquivo importado com ${dadosValidados.erros.length} erro(s) de validação.`
        }) as any);
      } else {
        dispatch(addAlert({
          severity: 'success',
          message: `Arquivo importado com ${dadosValidados.dados.length} códigos encontrados.`
        }) as any);
      }
    } catch (error: any) {
      const errorMessage = error.message ?? 'Erro desconhecido ao processar arquivo';
      setErrosValidacao([errorMessage]);
      dispatch(addAlert({
        severity: 'error',
        message: `Erro ao processar arquivo: ${errorMessage}`
      }) as any);
    } finally {
      setValidandoArquivo(false);
    }
  }, [dispatch, templateHeaders]);

  const validarDadosArquivo = useCallback((dadosParsed: ParsedFileResult): { dados: BloqueioGarantiaData[], erros: string[] } => {
    const dados: BloqueioGarantiaData[] = [];
    const erros: string[] = [];
    const codigosUnicos = new Set<string>();

    dadosParsed.rows.forEach((row, index) => {
      const linhaNumerada = index + 2;
      const codigo = row[0]?.trim();

      if (!codigo) {
        erros.push(`Linha ${linhaNumerada}: Código não pode estar vazio`);
        return;
      }

      if (codigosUnicos.has(codigo)) {
        erros.push(`Linha ${linhaNumerada}: Código "${codigo}" duplicado no arquivo`);
        return;
      }

      if (codigo.length < 3) {
        erros.push(`Linha ${linhaNumerada}: Código "${codigo}" muito curto (mínimo 3 caracteres)`);
        return;
      }

      codigosUnicos.add(codigo);
      dados.push({
        codigo,
        status: 'pending'
      });
    });

    return { dados, erros };
  }, []);

  const enviarParaBackend = useCallback(async () => {
    if (!file || dadosProcessados.length === 0) {
      dispatch(addAlert({
        severity: 'error',
        message: 'Nenhum arquivo válido para enviar.'
      }) as any);
      return;
    }



    try {
      setEnviandoParaBackend(true);
      setProgressoEnvio(0);
      setProcessamentoCompleto(false);

      const intervalId = setInterval(() => {
        setProgressoEnvio(prev => {
          if (prev >= 90) {
            clearInterval(intervalId);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const requestData: AdicionarBloqueioEmLoteRequest = {
        codigos: dadosProcessados.map(item => item.codigo),
        bloqueioTipo: BloqueioTipo.Garantia,
        constricaoTipo: undefined
      };

      const response = await investidorApi.postBloqueiosEmLote(dispatch, requestData, true);
      
      clearInterval(intervalId);
      setProgressoEnvio(100);
      const dadosComResposta = dadosProcessados.map(item => {
        const responseItem = response.find(r => r.codigo === item.codigo);
        return {
          ...item,
          custodianteGarantidor: responseItem?.bloqueioResponse?.razaoSocialCustodiante || '',
          cpfCnpjInvestidorGarantidor: responseItem?.bloqueioResponse?.cpfCnpjInvestidorGarantidor || '',
          contaDepositoGarantidor: responseItem?.bloqueioResponse?.contaDepositoGarantidor || '',
          codigoAtivo: item.codigo,
          status: responseItem?.sucesso ? 'success' as const : 'error' as const,
          mensagemErro: responseItem?.sucesso ? undefined : responseItem?.motivoFalha
        };
      });

      setDadosProcessados(dadosComResposta);
      setProcessamentoCompleto(true);

      const sucessos = dadosComResposta.filter(d => d.status === 'success').length;
      const erros = dadosComResposta.filter(d => d.status === 'error').length;

      dispatch(addAlert({
        severity: sucessos === dadosComResposta.length ? 'success' : 'warning',
        message: `Processamento concluído! ${sucessos} bloqueios processados com sucesso${erros > 0 ? `, ${erros} com erro` : ''}.`
      }) as any);

    } catch (error: any) {
      const errorMessage = error.message ?? 'Erro desconhecido ao enviar para o backend';
      dispatch(addAlert({
        severity: 'error',
        message: `Erro ao processar bloqueios em lote: ${errorMessage}`
      }) as any);
    } finally {
      setEnviandoParaBackend(false);
      setProgressoEnvio(0);
    }
  }, [file, dadosProcessados, dispatch, investidorApi]);

  const getResultsGridColumns = useCallback((): GridColDef[] => [
    {
      field: 'codigo',
      headerName: 'Código do Ativo',
      width: 150,
      sortable: true,
    },
    {
      field: 'custodianteGarantidor',
      headerName: 'Custodiante Garantidor',
      width: 200,
      sortable: true,
    },
    {
      field: 'cpfCnpjInvestidorGarantidor',
      headerName: 'CPF/CNPJ Investidor Garantidor',
      width: 220,
      sortable: true,
      renderCell: (params: GridCellParams) => {
        const cpfCnpj = params.value as string;
        return cpfCnpj ? CpfCnpjMask(cpfCnpj) : '';
      },
    },
    {
      field: 'contaDepositoGarantidor',
      headerName: 'Conta Depósito Garantidor',
      width: 200,
      sortable: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      sortable: true,
      renderCell: (params: GridCellParams) => {
        const status = params.value as string;
        return (
          <Chip
            label={status === 'success' ? 'Sucesso' : status === 'error' ? 'Erro' : 'Pendente'}
            color={status === 'success' ? 'primary' : status === 'error' ? 'secondary' : 'default'}
            size="small"
            icon={status === 'success' ? <CheckCircle /> : status === 'error' ? <Error /> : undefined}
          />
        );
      },
    },
    {
      field: 'mensagemErro',
      headerName: 'Mensagem de Erro',
      width: 250,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const mensagem = params.value as string;
        return mensagem ? (
          <Typography variant="caption" color="error">
            {mensagem}
          </Typography>
        ) : null;
      },
    },
  ], []);

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setHighlight(true);
  };

  const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setHighlight(false);
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setHighlight(false);
    
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      const selectedFile = files[0];
      
      if (!isValidFileType([selectedFile])) {
        handleFileTypeError();
        return;
      }
      
      if (!isValidFileSize(selectedFile)) {
        handleFileSizeError();
        return;
      }
      
      setFile(selectedFile);
      setErrosValidacao([]);
      processarArquivo(selectedFile);
    }
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length > 0) {
      const selectedFile = files[0];
      
      if (!isValidFileType([selectedFile])) {
        handleFileTypeError();
        return;
      }
      
      if (!isValidFileSize(selectedFile)) {
        handleFileSizeError();
        return;
      }
      
      setFile(selectedFile);
      setErrosValidacao([]);
      processarArquivo(selectedFile);
    }

    event.target.value = '';
  };

  return (
    <Container maxWidth="lg">
      <MdSpace />
      <Card style={{ marginBottom: '3rem' }}>
        <CardContent>
          <Grid container spacing={3}>
            {/* Header */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" mb={2}>
                <Button
                  startIcon={<ArrowBack />}
                  onClick={handleVoltar}
                  variant="outlined"
                  size="small"
                >
                  Voltar
                </Button>
                <Box ml={2}>
                  <Typography variant="h5">
                    Bloqueio de Garantia em Lote
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Importe um arquivo Excel com os códigos dos ativos para bloqueio em lote
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {!processamentoCompleto && (
              <>
                <Grid item xs={12}>
                  <Card variant="outlined" style={{ backgroundColor: '#f8f9fa', padding: '16px' }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="subtitle1" gutterBottom>
                          1. Baixar Planilha Modelo
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Baixe a planilha modelo com a estrutura necessária (coluna "Código")
                        </Typography>
                      </Box>
                      <Button
                        startIcon={<GetApp />}
                        onClick={handleBaixarPlanilhaModelo}
                        variant="contained"
                        color="primary"
                        disabled={false}
                        size="small"
                      >
                        Baixar Modelo
                      </Button>
                    </Box>
                  </Card>
                </Grid>


                {/* Área de Upload */}
                <Grid item xs={12}>
                  <Card variant="outlined" style={{ padding: '16px' }}>
                    <Typography variant="subtitle1" gutterBottom>
                      2. Importar Arquivo Excel
                    </Typography>
                  
                    <div
                      onClick={openFileDialog}
                      onDragOver={onDragOver}
                      onDragLeave={onDragLeave}
                      onDrop={onDrop}
                      style={{
                        width: '100%',
                        border: `2px dashed ${highlight ? '#1976d2' : file ? '#4caf50' : '#D7D8D9'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        textAlign: 'center',
                        padding: '40px 20px',
                        backgroundColor: highlight ? '#e3f2fd' : file ? '#f1f8e9' : 'transparent',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {file ? (
                        <CheckCircle fontSize="large" style={{ color: '#4caf50' }} />
                      ) : (
                        <AttachFile fontSize="large" color={highlight ? 'primary' : 'disabled'} />
                      )}
                      <MdSpace />
                      <Typography 
                        color={file ? 'primary' : highlight ? 'primary' : 'textSecondary'} 
                        variant="body1"
                      >
                        {file 
                          ? 'Arquivo carregado com sucesso!' 
                          : highlight
                          ? 'Solte o arquivo aqui'
                          : 'Arraste e solte o arquivo Excel ou clique para selecionar'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Formatos aceitos: {acceptedFileTypes.join(', ')} | Tamanho máximo: 10MB
                      </Typography>
                      
                      <input
                        ref={inputRef}
                        type="file"
                        accept={acceptedFileTypes.join(',')}
                        style={{ display: 'none' }}
                        onChange={onInputChange}
                      />
                    </div>

                    {file && (
                      <Box mt={3}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" p={2} bgcolor="#f1f8e9" borderRadius={1}>
                          <Box display="flex" alignItems="center">
                            <CheckCircle style={{ color: '#4caf50', marginRight: '8px' }} />
                            <Box>
                              <Typography variant="body2" style={{ fontWeight: 500 }}>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {(file.size / 1024).toFixed(2)} KB | {file.type || 'Arquivo Excel'}
                              </Typography>
                              {validandoArquivo && (
                                <Typography variant="caption" color="primary">
                                  Processando arquivo...
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
                            {validandoArquivo && <CircularProgress size={16} />}
                            <Chip 
                              label={validandoArquivo ? "Processando" : dadosProcessados.length > 0 ? "Importado" : "Pronto"} 
                              color={validandoArquivo ? "default" : dadosProcessados.length > 0 ? "primary" : "secondary"} 
                              size="small" 
                              icon={validandoArquivo ? undefined : <CheckCircle />}
                            />
                          </Box>
                        </Box>
                        
                        {/* Resultados da Validação */}
                        {dadosProcessados.length > 0 && (
                          <Box mt={2} p={2} bgcolor="#e8f5e8" borderRadius={1}>
                            <Typography variant="body2" style={{ fontWeight: 500, color: '#2e7d32' }}>
                              ✓ Arquivo importado: {dadosProcessados.length} códigos encontrados
                            </Typography>
                            <Box mt={1} maxHeight={150} overflow="auto">
                              {dadosProcessados.slice(0, 10).map((item, index) => (
                                <Typography key={index} variant="caption" display="block" color="textSecondary">
                                  • {item.codigo}
                                </Typography>
                              ))}
                              {dadosProcessados.length > 10 && (
                                <Typography variant="caption" color="textSecondary">
                                  ... e mais {dadosProcessados.length - 10} códigos
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                    
                    {errosValidacao.length > 0 && (
                      <Box mt={2}>
                        <Box p={2} bgcolor="#ffebee" borderRadius={1}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <Error style={{ color: '#f44336', marginRight: '8px' }} />
                            <Typography variant="body2" style={{ fontWeight: 500, color: '#f44336' }}>
                              Erros de Validação:
                            </Typography>
                          </Box>
                          {errosValidacao.map((erro, index) => (
                            <Typography key={index} variant="body2" color="error">
                              • {erro}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Card>
                </Grid>

                {/* Botões de Ação */}
                {file && dadosProcessados.length > 0 && !processamentoCompleto && (
                  <Grid item xs={12}>
                    <Card variant="outlined" style={{ backgroundColor: '#fafafa', padding: '16px' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle1">
                            3. Enviar para Processamento
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {dadosProcessados.length} códigos serão enviados para processamento em lote
                          </Typography>
                          {enviandoParaBackend && (
                            <Box mt={1}>
                              <Typography variant="caption" color="primary">
                                Enviando... {progressoEnvio}%
                              </Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={progressoEnvio} 
                                style={{ marginTop: '4px', height: '4px', borderRadius: '2px' }}
                              />
                            </Box>
                          )}
                        </Box>
                        <Box display="flex" style={{ gap: '8px' }}>
                          <Button
                            variant="outlined"
                            startIcon={<Delete />}
                            onClick={() => {
                              setFile(undefined);
                              setErrosValidacao([]);
                              setDadosProcessados([]);

                            }}
                            size="small"
                            disabled={enviandoParaBackend}
                          >
                            Cancelar
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            disabled={enviandoParaBackend || errosValidacao.length > 0}
                            startIcon={enviandoParaBackend ? <CircularProgress size={16} /> : undefined}
                            onClick={enviarParaBackend}
                          >
                            {enviandoParaBackend ? 'Enviando...' : 'Enviar para Processamento'}
                          </Button>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                )}
              </>
            )}

            {/* Área de Resultados */}
            {processamentoCompleto && dadosProcessados.length > 0 && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Typography variant="h6">
                          Resultados do Processamento
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {dadosProcessados.filter(d => d.status === 'success').length} bloqueios processados com sucesso, 
                          {dadosProcessados.filter(d => d.status === 'error').length} com erro
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={() => {
                          setProcessamentoCompleto(false);
                          setDadosProcessados([]);
                          setFile(undefined);
                          setErrosValidacao([]);
                        }}
                        size="small"
                      >
                        Novo Processamento
                      </Button>
                    </Box>
                    
                    <DataGridPro
                      autoHeight
                      localeText={localizedTextsMap}
                      componentsProps={componentsPropsValues}
                      rows={dadosProcessados.map((item, index) => ({
                        id: index,
                        ...item
                      }))}
                      columns={getResultsGridColumns()}
                      components={{
                        Toolbar: CustomToolBarUtils,
                      }}
                      disableSelectionOnClick
                      rowsPerPageOptions={rowsPerPageOptionsValues}
                      pagination
                      pageSize={10}
                      getRowClassName={(params) => 
                        params.row.status === 'error' ? 'error-row' : 
                        params.row.status === 'success' ? 'success-row' : ''
                      }
                      sx={{
                        '& .error-row': {
                          backgroundColor: 'rgba(255, 0, 0, 0.1)',
                        },
                        '& .success-row': {
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default BloqueiosGarantiaEmLote;
